import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nProvider";
import { Calendar, Filter, Users, Building, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { useActiveAds } from "@/hooks/useActiveAds";
import { allPartiesIncludingNonParliamentary } from "@/data/parties";
import { getValidatedPartyDisplayData, usePartiesStore } from "@/data/partiesStore";
import { isStagingModeEnabled } from "@/utils/stagingMode";
import { useCmsSection } from "@/hooks/useCmsSection";
import { useEventsStore } from "@/data/eventsStore";

export type PartyEvent = {
  id: string;
  partyAcronym: string;
  organizerStatus: string;
  title: string;
  description: string;
  date: string;
  location: string;
  type: "conference" | "meeting" | "formation" | "campagne" | "autre";
  status: "upcoming" | "ongoing" | "past";
  imageUrl?: string;
  registrationType?: "public" | "mandatory";
};

type TranslatedEvent = {
  id: string;
  partyAcronym: string;
  organizerStatus: string;
  title: Record<string, string>;
  description: Record<string, string>;
  date: string;
  location: Record<string, string>;
  type: "conference" | "meeting" | "formation" | "campagne" | "autre";
  status: "upcoming" | "ongoing" | "past";
  imageUrl?: string;
  registrationType?: "public" | "mandatory";
};



const typeColors: Record<string, string> = {
  conference: "bg-primary/10 text-primary border-primary/20",
  meeting: "bg-accent/10 text-accent-foreground border-accent/20",
  formation: "bg-secondary/10 text-secondary border-secondary/20",
  campagne: "bg-destructive/10 text-destructive border-destructive/20",
  autre: "bg-muted text-muted-foreground border-border",
};

const typeLabels: Record<string, Record<string, string>> = {
  fr: { conference: "Conférence", meeting: "Réunion", formation: "Formation", campagne: "Campagne", autre: "Autre" },
  ar: { conference: "مؤتمر", meeting: "اجتماع", formation: "تكوين", campagne: "حملة", autre: "أخرى" },
  zgh: { conference: "ⴰⴳⵔⴰⵡ", meeting: "ⴰⵎⵙⴰⵡⴰⵍ", formation: "ⴰⵙⵎⵖⵔ", campagne: "ⵜⴰⵎⵀⵍⴰ", autre: "ⵢⴰⴹⵏ" },
};

const PartiesEnActionSection = () => {
  const { lang, isRtl } = useI18n();
  const [localEvents, setLocalEvents] = useState<PartyEvent[]>([]);
  const cms = useCmsSection("parties-action");
  const profiles = usePartiesStore(state => state.profiles);

  const { events: backendEvents, fetchEvents } = useEventsStore();

  useEffect(() => {
    fetchEvents(true); // true = public events
  }, [fetchEvents]);

  useEffect(() => {
    const isStaging = isStagingModeEnabled();
    const approvedAndFormatted = backendEvents
      .filter((e: any) => e.status === "approved" && (isStaging || e.publishStatus !== "draft"))
      .map((e: any) => ({
        id: e.id,
        partyAcronym: e.partyAcronym,
        organizerStatus: e.organizerStatus || "parti",
        title: e.title,
        description: e.description,
        date: e.date,
        location: e.location,
        type: e.type,
        imageUrl: e.imageUrl,
        registrationType: e.registrationType || "public",
        status: "upcoming" as const,
      }));
    setLocalEvents(approvedAndFormatted);

    const handleStaging = () => {
       const isStaging = isStagingModeEnabled();
       const approvedAndFormatted = backendEvents
         .filter((e: any) => e.status === "approved" && (isStaging || e.publishStatus !== "draft"))
         .map((e: any) => ({
           id: e.id,
           partyAcronym: e.partyAcronym,
           organizerStatus: e.organizerStatus || "parti",
           title: e.title,
           description: e.description,
           date: e.date,
           location: e.location,
           type: e.type,
           imageUrl: e.imageUrl,
           registrationType: e.registrationType || "public",
           status: "upcoming" as const,
         }));
       setLocalEvents(approvedAndFormatted);
    };
    window.addEventListener("stagingModeChanged", handleStaging);
    return () => window.removeEventListener("stagingModeChanged", handleStaging);
  }, [backendEvents]);

  const events = useMemo(() => localEvents, [localEvents]);

  // Split logic
  const partisEvents = useMemo(() => events.filter(e => e.organizerStatus === 'parti'), [events]);
  const citoyensEvents = useMemo(() => events.filter(e => ['ong','citoyen','institution'].includes(e.organizerStatus)), [events]);

  const sectionLabels = {
    fr: { 
      tag: "Événements",
      titlePartis: "Événements des Partis", 
      titleCitoyens: "Événements Citoyens et Institutionnels",
      description: "Découvrez les activités organisées par les formations politiques, la société civile et les institutions.", 
      proposeBtn: "Proposer un événement", 
      viewAll: "Voir tous les événements"
    },
    ar: { 
      tag: "فعاليات",
      titlePartis: "فعاليات الأحزاب", 
      titleCitoyens: "الفعاليات المواطنة والمؤسساتية",
      description: "اكتشفوا الأنشطة التي تنظمها الهيئات السياسية والمجتمع المدني.", 
      proposeBtn: "اقتراح فعالية", 
      viewAll: "عرض كل الفعاليات"
    },
    zgh: { 
      tag: "ⵉⵎⵓⵙⵙⵓⵜⵏ",
      titlePartis: "ⵉⵎⵓⵙⵙⵓⵜⵏ ⵏ ⵉⵎⵓⵔⴰⵏ", 
      titleCitoyens: "ⵉⵎⵓⵙⵙⵓⵜⵏ ⵏ ⵉⵏⴰⵎⵓⵔⵏ",
      description: "ⵉⵙⵙⵏ ⵜⵉⴳⴰⵡⵉⵏ ⵏ ⵉⵎⵓⵔⴰⵏ ⵉⵙⵔⵜⴰⵏⵏ ⴷ ⵡⴰⵎⵓⵏ ⵓⵖⵔⵉⵎ.", 
      proposeBtn: "ⵙⵉⴷⴼ ⴰⵎⵓⵙⵙⵓ", 
      viewAll: "ⵥⵕ ⵎⴰⵕⵕⴰ ⵉⵎⵓⵙⵙⵓⵜⵏ"
    },
  };

  const labels = sectionLabels[lang as keyof typeof sectionLabels] || sectionLabels.fr;

  const formatDateBox = (dateStr: string) => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return { m: "", d: "" };
    const m = d.toLocaleDateString(lang === 'fr' ? 'fr-FR' : lang === 'ar' ? 'ar-MA' : 'en-US', { month: 'short' });
    const day = d.getDate();
    return { m: m.toUpperCase(), d: day.toString() };
  };

  const EventCard = ({ event }: { event: PartyEvent }) => {
    const dateObj = formatDateBox(event.date);
    const isParti = event.organizerStatus === 'parti';

    return (
        <Link key={event.id} to={`/evenement/${event.id}`} className="group block h-full">
            <div className="flex flex-col h-full bg-card rounded-xl overflow-hidden border border-border shadow-sm hover:shadow-md transition-shadow">
            <div className="w-full h-40 bg-muted relative overflow-hidden shrink-0">
                {event.imageUrl ? (
                <img src={event.imageUrl} alt={event.title} className="w-full h-full object-contain bg-white transition-transform duration-700 group-hover:scale-105" />
                ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-transparent">
                    <Calendar className="w-10 h-10 text-primary/20" />
                </div>
                )}
                {dateObj.d && (
                <div className="absolute top-2 start-2 bg-background/95 backdrop-blur-sm shadow-md rounded-lg flex flex-col items-center justify-center min-w-[40px] p-1.5 z-10">
                    <span className="text-destructive font-bold text-[10px] leading-none mb-1">{dateObj.m}</span>
                    <span className="text-foreground font-display font-black text-lg leading-none">{dateObj.d}</span>
                </div>
                )}
                <Badge className={`absolute bottom-2 end-2 text-[10px] uppercase tracking-wide z-10 ${typeColors[event.type].replace('bg-', 'bg-background/90 text-').replace('text-', 'text-')}`}>
                {typeLabels[lang][event.type]}
                </Badge>
            </div>
            <div className="flex flex-col flex-1 p-3">
                <h3 className="font-display font-bold text-foreground text-sm leading-tight mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                {event.title}
                </h3>
                <p className="text-muted-foreground font-body text-xs mb-3 truncate">
                 {event.location}
                </p>
                <div className="flex items-center gap-2 mt-auto pt-2 border-t border-border/50">
                    <span className="inline-flex shrink-0 items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold bg-white border border-border overflow-hidden shadow-sm">
                        {event.organizerLogo ? (
                            <img src={event.organizerLogo} alt={event.partyAcronym} className="w-full h-full object-contain p-0.5" />
                        ) : isParti && getValidatedPartyDisplayData(event.partyAcronym, event.partyAcronym, '').logoUrl ? (
                            <img src={getValidatedPartyDisplayData(event.partyAcronym, event.partyAcronym, '').logoUrl} alt={event.partyAcronym} className="w-full h-full object-contain p-0.5" />
                        ) : isParti ? (
                            allPartiesIncludingNonParliamentary.find(p => p.acronym === event.partyAcronym)?.symbol || event.partyAcronym.slice(0,2)
                        ) : event.organizerStatus === 'institution' ? <Building className="w-3 h-3 text-muted-foreground"/> : event.organizerStatus === 'ong' ? <Users className="w-3 h-3 text-muted-foreground"/> : <Shield className="w-3 h-3 text-muted-foreground"/>}
                    </span>
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide truncate">
                        {event.partyAcronym}
                    </span>
                </div>
            </div>
            </div>
        </Link>
    );
  };

  return (
    <section id="evenements" className="py-10 bg-background relative overflow-hidden flex flex-col gap-8">
      
      {/* HEADER GLOBALE */}
      <div className="container">
        <motion.div
          className="text-center mb-8 max-w-2xl mx-auto flex flex-col items-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-secondary/10 text-secondary font-body text-base md:text-lg font-extrabold mb-1.5 border border-secondary/20 animate-pulse tracking-widest uppercase">
            {labels.tag}
          </span>
          <h2 className="text-3xl md:text-5xl font-display font-extrabold text-foreground tracking-tight">
            {lang === 'ar' ? (cms.title_ar || "الأجندة الوطنية") : lang === 'zgh' ? "ⴰⵖⴰⵡⴰⵙ ⴰⵏⴰⵎⵓⵔ" : (cms.title_fr || <>Agenda <span className="text-gradient-warm">National</span></>)}
          </h2>
          <p className="mt-1.5 text-muted-foreground font-body max-w-xl mx-auto text-lg leading-relaxed mb-2">
            {lang === 'ar' ? (cms.subtitle_ar || labels.description) : (cms.subtitle_fr || labels.description)}
          </p>
          <Button variant="secondary" className="font-body font-semibold shadow-sm" asChild>
            <Link to="/proposer-evenement">{labels.proposeBtn}</Link>
          </Button>
        </motion.div>
      </div>

      {/* SECTION 1 : PARTIS */}
      <div className="container">
          <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl md:text-2xl font-display font-bold flex items-center gap-2 leading-tight">
                  <div className="w-2 h-5 md:h-6 bg-primary rounded-full shrink-0"></div>
                  {labels.titlePartis}
              </h3>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
              {partisEvents.slice(0, 8).map(e => <EventCard key={e.id} event={e} />)}
          </div>
          
          <div className="flex justify-center">
              <Button asChild className="rounded-full shadow-md"><Link to="/evenements/partis">{labels.viewAll} ({partisEvents.length})</Link></Button>
          </div>
      </div>

      {/* SECTION 2 : CITOYENS ET INSTITUTIONS */}
      <div className="container">
          <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl md:text-2xl font-display font-bold flex items-center gap-2 leading-tight">
                  <div className="w-2 h-5 md:h-6 bg-blue-500 rounded-full shrink-0"></div>
                  {labels.titleCitoyens}
              </h3>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
              {citoyensEvents.slice(0, 8).map(e => <EventCard key={e.id} event={e} />)}
          </div>

          <div className="flex justify-center">
              <Button asChild className="rounded-full shadow-md bg-blue-600 hover:bg-blue-700 text-white"><Link to="/evenements/citoyens">{labels.viewAll} ({citoyensEvents.length})</Link></Button>
          </div>
      </div>

    </section>
  );
};

export default PartiesEnActionSection;
